<!-- 
Dropdown menu showing warnings for the levy collection.
-->
<template>
  <v-menu v-if="hasWarnings" v-model="menuOpen" offset-y>
    <template v-slot:activator="{ on }">
      <v-btn 
        :large="large" :block="xs" :class="css" class="mb-3" 
        v-on="on"
      >
        <v-icon color="error" class="mr-2">warning</v-icon>
        Warnings ({{ warningCount }}) 
        <our-expand-icon :expanded="menuOpen"/>
      </v-btn>
    </template>
    <v-list>
      <div style="height: 5px">
        <v-progress-linear v-if="opening" indeterminate color="error" />
      </div>
      <v-list-tile 
        v-for="(warning, index) in warnings" 
        :key="index" 
        :inactive="opening" 
        @click.stop="showProviders(warning.type)"
      >
        <v-list-tile-title :class="{'grey--text': opening}">
          <b>{{ warning.count }} </b>
          <span>{{ pluralise(warning.count) }}</span>
          <span> 
            {{ getWarningVerb(warning.type, warning.count) }}
          </span>
          <span>
            {{ lookups.levyCollectionProviderWarningTypeDescriptions[warning.type] | startWithLowerCase }}.
          </span>
        </v-list-tile-title>
      </v-list-tile>
      <v-divider/>
      <v-list-tile 
        :inactive="opening" 
        @click.stop="showAllProviders"
      >
        <v-list-tile-title :class="{'grey--text': opening}">
          Show all providers with warnings
        </v-list-tile-title>
      </v-list-tile>
    </v-list>
  </v-menu>
</template>

<script>
/* 
Warnings dropdown menu
----------------------
Shows warnings for a levy collection in a dropdown menu. Clicking a warning will bring up the 
appropriate listing, filtered to show records with the warning.
*/
import _ from "underscore";
import lookupMixins from "@/mixins/lookup.mixins";
import filterMixins from "@/mixins/filter.mixins";
import taskMixins from "@/mixins/task.mixins";
import * as lcpAction from "@/store/levy-collection-providers/action-types";
import * as lcpMutation from "@/store/levy-collection-providers/mutation-types";

export default {
  mixins: [lookupMixins, taskMixins, filterMixins],
  props: {
    levyCollectionId: {
      type: Number,
      required: true
    },
    large: {
      type: Boolean,
      default: false
    },
    css: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      menuOpen: false
    };
  },
  computed: {
    state() {
      return this.$store.state.levyCollectionProviders.warnings;
    },
    warnings() {
      return _(this.state.data).filter(d => d.count > 0 && !this.isForInformationOnly(d.type));
    },
    loaded() {
      return this.state.loaded;
    },
    hasWarnings() {
      return this.state.loaded && this.warnings.length > 0;
    },
    warningCount() {
      return _(this.warnings).reduce((memo, warning) => memo + warning.count, 0);
    },
    opening() {
      return this.$store.state.levyCollectionProviders.listing.loading;
    },
    xs() {
      return this.$vuetify.breakpoint.xsOnly;
    }
  },
  watch: {
    levyCollectionId() {
      this.reload();
    },
    loaded() {
      this.reload();
    }
  },
  mounted() {
    this.reload();
  },
  methods: {
    reload() {
      this.$store.dispatch(lcpAction.WARNINGS_REFRESH, this.levyCollectionId);
    },
    showProviders(warningType) {
      this.searchForProvidersByWarningTypes([warningType]);
    },
    showAllProviders() {
      const warningTypes = this.getLookupValuesWithoutAttribute(
        this.lookups.levyCollectionProviderWarningTypes,
        "ForInformationOnly"
      );
      this.searchForProvidersByWarningTypes(warningTypes);
    },
    getDescription(warning) {
      return this.lookups.levyCollectionProviderWarningTypeDescriptions[warning.type];
    },
    pluralise(count) {
      return `provider${count == 1 ? "" : "s"}`;
    },
    getWarningVerb(type, count) {
      if (type == "Paused") return count == 1 ? "is" : "are";
      return count == 1 ? "has" : "have";
    },
    async searchForProvidersByWarningTypes(warningTypes) {
      this.$store.commit(lcpMutation.LIST_CLEAR_CRITERIA);
      this.$store.commit(lcpMutation.LIST_SET_CRITERIA, { levyCollectionProviderWarningTypes: warningTypes });
      this.$store.commit(lcpMutation.LIST_SET_FILTERS, { levyCollectionId: this.levyCollectionId });
      await this.$store.dispatch(lcpAction.LIST);
      this.menuOpen = false;
      const url = `/levy-collections/${this.levyCollectionId}/providers`;
      if (this.$route.path != url) {
        this.$router.push(url);
      }
    },
    isForInformationOnly(warningType) {
      return this.doesLookupHaveAttribute(
        warningType,
        "ForInformationOnly",
        this.lookups.levyCollectionProviderWarningTypes
      );
    }
  }
};
</script>
